import React, { useEffect, useState } from "react";
import { 
  GetDataFromApiWithParams,
  UpdateAndGetResponse,
  DeleteAndGetResponse
} from "../../../../services/api/ExecuteApiRequests";
import { 
  Box, 
  Button, 
  IconButton, 
  Menu, 
  MenuItem, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Grid,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Container,
  Divider
} from "@mui/material";
import { 
  Search as SearchIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Description as DescriptionIcon,
  AttachFile as AttachFileIcon,
  Male as MaleIcon,
  Female as FemaleIcon
} from "@mui/icons-material";
import useDecodedUser from "../../../../services/hooks/useDecodedUser";
import { IPDCToastMessageResult } from "../../../../components/Controls";

// EditableTableCell component for inline editing
const EditableTableCell = ({ record, field, value, isEditing, onFieldChange, fieldType = "text", options }) => {
  if (isEditing) {
    // For date fields
    if (fieldType === "date") {
      return (
        <TableCell>
          <TextField
            size="small"
            type="date"
            value={value ? new Date(value).toISOString().split('T')[0] : ''}
            onChange={(e) => onFieldChange(field, e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: '100%' }}
          />
        </TableCell>
      );
    }
    
    // For numeric fields
    if (fieldType === "number") {
      return (
        <TableCell>
          <TextField
            size="small"
            type="number"
            value={value || 0}
            onChange={(e) => onFieldChange(field, parseInt(e.target.value) || 0)}
            sx={{ width: '80px' }}
            inputProps={{ min: 0 }}
          />
        </TableCell>
      );
    }
    
    // For text fields
    if (fieldType === "text") {
      return (
        <TableCell>
          <TextField
            size="small"
            value={value || ''}
            onChange={(e) => onFieldChange(field, e.target.value)}
            sx={{ width: '100%' }}
            multiline={field === "description"}
            rows={field === "description" ? 2 : 1}
          />
        </TableCell>
      );
    }
    
    // For select fields
    if (fieldType === "select" && options) {
      return (
        <TableCell>
          <Select
            size="small"
            value={value || ''}
            onChange={(e) => onFieldChange(field, e.target.value)}
            sx={{ width: '150px' }}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </TableCell>
      );
    }
    
    // For status field (select)
    if (fieldType === "status") {
      return (
        <TableCell>
          <Select
            size="small"
            value={value || 'pending'}
            onChange={(e) => onFieldChange(field, e.target.value)}
            sx={{ width: '120px' }}
          >
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="declined">Declined</MenuItem>
            <MenuItem value="requested">Requested</MenuItem>
          </Select>
        </TableCell>
      );
    }
    
    // Default to regular cell for non-editable fields
    return <TableCell>{value}</TableCell>;
  }
  
  // Non-editable mode - display values
  if (fieldType === "date" && value) {
    return <TableCell>{new Date(value).toLocaleDateString()}</TableCell>;
  }
  
  if (fieldType === "number") {
    return <TableCell>{value || 0}</TableCell>;
  }
  
  return <TableCell>{value || ''}</TableCell>;
};

export default function ApproveInvestorActivity() {
  // Main states
  const [activityData, setActivityData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Status filter states
  const [statusFilter, setStatusFilter] = useState("all");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  
  // Row selection and action states
  const [selectedRow, setSelectedRow] = useState(null);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  
  // Dialog states
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [remarks, setRemarks] = useState("");
  
  // Processing states
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Inline editing states - using actual 'no' from API as identifier
  const [editingId, setEditingId] = useState(null);
  const [editedRowData, setEditedRowData] = useState(null);
  const [originalRowData, setOriginalRowData] = useState(null);
  
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Toast Notification State
  const [toastMessage, setToastMessage] = useState({ message: "", type: "" });
  const [parkIdState, setParkIdState] = useState("");
  
  const decodedUser = useDecodedUser();
  
  // Toast Notification Functions
  const showToast = (message, type = "success") => {
    setToastMessage({ message, type });
  };

  const showSuccessToast = (message) => {
    showToast(message, "success");
  };

  const showErrorToast = (message) => {
    showToast(message, "error");
  };

  const showWarningToast = (message) => {
    showToast(message, "warning");
  };

  const showInfoToast = (message) => {
    showToast(message, "warning");
  };

  // Get status color
  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case "approved": return "success";
      case "declined": return "error";
      case "requested": return "info";
      default: return "default";
    }
  };

  // Get chip color
  const getChipColor = (status) => {
    switch(status?.toLowerCase()) {
      case "approved": return "success";
      case "declined": return "error";
      case "requested": return "info";
      default: return "default";
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Fetch investor activity data - PRESERVES API's 'no' field
  const fetchInvestorActivityData = async () => {
    if (!parkIdState) {
      console.error("No parkIdState available");
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      // Use the same API endpoint pattern as the provided code
      const response = await GetDataFromApiWithParams("/investors/allapproveInvestorsactivity");
      
      if (response && Array.isArray(response)) {
        console.log("Investor activity data fetched:", response);
        
        // Format dates for display - PRESERVE the original 'no' from API
        const formattedData = response.map((item) => {
          // Check if item already has a 'no' field from API
          // If not, try to use 'id' or create a fallback
          let activityNo = item.no;
          if (activityNo === undefined) {
            // Try other possible identifier fields
            activityNo = item.id || item.activity_id || item.activity_no;
          }
          
          return {
            ...item,
            no: activityNo, // Use API's identifier or null
            recorded_on_formatted: formatDate(item.recorded_on),
            activity_period_start_from_formatted: formatDate(item.activity_period_start_from),
            activity_period_to_formatted: formatDate(item.activity_period_to),
            status: item.status || "pending"
          };
        }).filter(item => item.no !== null && item.no !== undefined); // Filter out items without identifier
        
        setActivityData(formattedData);
        setSearchResults(formattedData);
        setTotalRecords(formattedData.length);
        
        showSuccessToast("Investor activity records loaded successfully!");
      } else {
        setActivityData([]);
        setSearchResults([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching investor activity data:", error);
      setActivityData([]);
      setSearchResults([]);
      setTotalRecords(0);
      showErrorToast("Failed to load investor activity records");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("Code 2 - useEffect triggered, decodedUser:", decodedUser);
    
    // Check if decodedUser is available and has park_id
    if (decodedUser && decodedUser.park_id) {
      const parkId = decodedUser.park_id;
      console.log("Setting parkIdState to:", parkId);
      setParkIdState(parkId);
      fetchInvestorActivityData();
    }
  }, [decodedUser?.park_id]);

  // Search function - PRESERVES API's 'no' field
  const handleSearch = async () => {
    if (!parkIdState) {
      showErrorToast("Park information not available. Please try again.");
      return;
    }
    
    try {
      setIsSearching(true);
      
      console.log("Search query:", searchQuery);
      
      if (!searchQuery.trim()) {
        // If search is empty, fetch all records
        await fetchInvestorActivityData();
        return;
      }
      
      // Use the same API call pattern as the provided code
      const response = await GetDataFromApiWithParams("investors/searchactivity", [parkIdState, searchQuery]);
      
      if (response && Array.isArray(response)) {
        // Format WITHOUT overriding the 'no' field - preserve API value
        const formattedData = response.map((item) => {
          // Preserve the API's 'no' field if it exists
          let activityNo = item.no;
          if (activityNo === undefined) {
            // Try other possible identifier fields
            activityNo = item.id || item.activity_id || item.activity_no;
          }
          
          return {
            ...item,
            no: activityNo,
            recorded_on_formatted: formatDate(item.recorded_on),
            activity_period_start_from_formatted: formatDate(item.activity_period_start_from),
            activity_period_to_formatted: formatDate(item.activity_period_to),
            status: item.status || "pending"
          };
        }).filter(item => item.no !== null && item.no !== undefined);
        
        setSearchResults(formattedData);
        setTotalRecords(formattedData.length);
        setPage(0);
        
        const resultCount = formattedData.length;
        if (resultCount > 0) {
          showSuccessToast(`Found ${resultCount} investor activity records!`);
        } else {
          showWarningToast("No records found for your search.");
        }
      } else {
        setSearchResults([]);
        setTotalRecords(0);
        showWarningToast("No records found for your search.");
      }
    } catch (error) {
      console.error("Error searching investor activities:", error);
      setSearchResults([]);
      setTotalRecords(0);
      showErrorToast("Search failed! Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  // Handle enter key in search
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Apply status filters
  const applyStatusFilter = (status) => {
    setStatusFilter(status);
    let filtered = [...activityData];
    
    if (status !== "all") {
      filtered = filtered.filter(item => 
        (item.status || "pending").toLowerCase() === status.toLowerCase()
      );
    }
    
    // Also apply search query if exists
    if (searchQuery.trim()) {
      const searchValue = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(item => 
        (item.no && item.no.toString().toLowerCase().includes(searchValue)) ||
        (item.investor_id && item.investor_id.toLowerCase().includes(searchValue)) ||
        (item.employment && item.employment.toLowerCase().includes(searchValue)) ||
        (item.description && item.description.toLowerCase().includes(searchValue)) ||
        (item.park_id && item.park_id.toLowerCase().includes(searchValue))
      );
    }
    
    setSearchResults(filtered);
    setTotalRecords(filtered.length);
    setPage(0);
    setFilterAnchorEl(null);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    fetchInvestorActivityData();
    showWarningToast("All filters cleared");
  };

  // Start inline editing - using actual 'no' from API as identifier
  const handleInlineEdit = (record) => {
    // If we're already editing a different row, cancel that edit first
    if (editingId && editingId !== record.no) {
      handleCancelInlineEdit();
    }
    
    setEditingId(record.no); // Use the actual 'no' from API
    setOriginalRowData({...record});
    setEditedRowData({...record});
  };

  // Save inline edits - using actual 'no' from API
  const handleSaveInlineEdit = async () => {
    if (!editedRowData) return;
    
    try {
      setIsProcessing(true);
      
      // Prepare the data for update
      const updateData = {
        ...editedRowData,
        recorded_on: editedRowData.recorded_on,
        activity_period_start_from: editedRowData.activity_period_start_from,
        activity_period_to: editedRowData.activity_period_to,
        // Remove formatted fields
        recorded_on_formatted: undefined,
        activity_period_start_from_formatted: undefined,
        activity_period_to_formatted: undefined
      };
      
      console.log("Sending data to update:", updateData);
      console.log("Updating record with actual no from API:", editingId);
      
      // Use the actual 'no' from API response
      const result = await UpdateAndGetResponse(`investors/activities/${editingId}`, updateData);
      
      if (result) {
        // Update the search results using the actual 'no' from API
        setSearchResults(prevResults => 
          prevResults.map(record => 
            record.no === editingId ? { ...record, ...updateData } : record
          )
        );
        
        // Also update activityData
        setActivityData(prevData =>
          prevData.map(item =>
            item.no === editingId ? { ...item, ...updateData } : item
          )
        );
        
        // Exit edit mode
        setEditingId(null);
        setEditedRowData(null);
        setOriginalRowData(null);
        
        showSuccessToast("Record updated successfully!");
      }
    } catch (error) {
      console.error("Error saving record:", error);
      showErrorToast("Failed to save changes!");
    } finally {
      setIsProcessing(false);
    }
  };

  // Cancel inline editing
  const handleCancelInlineEdit = () => {
    setEditingId(null);
    setEditedRowData(null);
    setOriginalRowData(null);
  };

  // Handle field changes in inline editing
  const handleFieldChange = (fieldName, value) => {
    setEditedRowData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Delete record - using actual 'no' from API
  const handleDeleteRecord = async (recordNo) => {
    if (window.confirm("Are you sure you want to delete this investor activity record?")) {
      try {
        setIsProcessing(true);
        
        // Use the actual 'no' from API
        await DeleteAndGetResponse(`investors/activities/${recordNo}`);
        
        // Update local state using the actual 'no'
        setActivityData(prevData => prevData.filter(item => item.no !== recordNo));
        setSearchResults(prevData => prevData.filter(item => item.no !== recordNo));
        setTotalRecords(prev => prev - 1);
        
        // If we're deleting the record that's being edited, cancel edit mode
        if (editingId === recordNo) {
          setEditingId(null);
          setEditedRowData(null);
          setOriginalRowData(null);
        }
        
        showSuccessToast("Investor activity record deleted successfully!");
      } catch (error) {
        console.error("Error deleting record:", error);
        showErrorToast("Failed to delete investor activity record!");
      } finally {
        setIsProcessing(false);
      }
    }
  };

  // View record details
  const handleViewRecord = (record) => {
    setSelectedRow(record);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedRow(null);
  };

  // Action menu handlers
  const handleActionMenuOpen = (event, row) => {
    setActionMenuAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
  };

  // Action handlers for MoreVert menu
  const handleApproveClick = () => {
    setApprovalDialogOpen(true);
    handleActionMenuClose();
  };

  const handleDeclineClick = () => {
    setDeclineDialogOpen(true);
    handleActionMenuClose();
  };

  const handleRequestClick = () => {
    setRequestDialogOpen(true);
    handleActionMenuClose();
  };

  const handleViewDetails = (row) => {
    handleViewRecord(row);
    handleActionMenuClose();
  };

  // API call to update status - using actual 'no' from API
  const updateActivityStatus = async (activityNo, newStatus, remarks = "") => {
    setIsProcessing(true);
    try {
      // API call - using actual 'no' from API
      const updateData = {
        no: activityNo,
        status: newStatus,
        remarks: remarks
      };
      
      const result = await UpdateAndGetResponse(`investors/approveactivity/${activityNo}`, updateData);
      
      if (result) {
        // Update local state using actual 'no'
        setActivityData(prevData =>
          prevData.map(item =>
            item.no === activityNo
              ? { 
                  ...item, 
                  ...result,
                  // Preserve the 'no' field from existing item
                  no: item.no
                }
              : item
          )
        );
        
        // Update search results using actual 'no'
        setSearchResults(prevResults => 
          prevResults.map(item =>
            item.no === activityNo
              ? { 
                  ...item, 
                  ...result,
                  // Preserve the 'no' field from existing item
                  no: item.no
                }
              : item
          )
        );
        
        showSuccessToast(`Investor activity ${newStatus} successfully!`);
        return true;
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showErrorToast(`Failed to ${newStatus} investor activity`);
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  // Dialog handlers
  const handleVerifyConfirm = async () => {
    if (!selectedRow) {
      showErrorToast("No record selected!");
      setApprovalDialogOpen(false);
      return;
    }
       
    const success = await updateActivityStatus(selectedRow.no, "Approved", remarks);
    if (success) {
      setApprovalDialogOpen(false);
      setRemarks("");
      setSelectedRow(null);
      fetchInvestorActivityData();
    }
  };

  const handleDeclineConfirm = async () => {
    if (!selectedRow) {
      showErrorToast("No record selected!");
      setDeclineDialogOpen(false);
      return;
    }
    
    const success = await updateActivityStatus(selectedRow.no, "declined", remarks);
    if (success) {
      setDeclineDialogOpen(false);
      setRemarks("");
      setSelectedRow(null);
    }
  };

  const handleRequestConfirm = async () => {
    if (!selectedRow) {
      showErrorToast("No record selected!");
      setRequestDialogOpen(false);
      return;
    }
    
    const success = await updateActivityStatus(selectedRow.no, "requested", remarks);
    if (success) {
      setRequestDialogOpen(false);
      setRemarks("");
      setSelectedRow(null);
    }
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Refresh activities list
  const handleRefreshActivities = () => {
    fetchInvestorActivityData();
  };

  // Calculate total employment
  const calculateTotalEmployment = (record) => {
    return (record.emp_prof_male || 0) + 
           (record.emp_prof_female || 0) + 
           (record.emp_labor_male || 0) + 
           (record.emp_labor_female || 0);
  };

  // Check if parkIdState is available before rendering search/table
  const isParkAvailable = !!parkIdState;

  // Calculate paginated data
  const paginatedData = searchResults.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container>
      {/* Toast Notification */}
      {toastMessage.message && (
        <IPDCToastMessageResult 
          message={toastMessage.message} 
          type={toastMessage.type} 
        />
      )}

      {/* Main Title */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Investor Activity Approval
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review and approve investor activity records
        </Typography>
        
        {/* Debug info - can be removed in production */}
        {!isParkAvailable && (
          <Box sx={{ mt: 2, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="body2" color="warning.dark">
              ⚠️ Waiting for user information to load...
            </Typography>
          </Box>
        )}
      </Box>
          
      {/* Search Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search by Investor ID, Description, Activity Type, or Park ID"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
              variant="outlined"
              disabled={!isParkAvailable || loading}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              disabled={isSearching || loading || !isParkAvailable}
              startIcon={<SearchIcon />}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleRefreshActivities}
              startIcon={<RefreshIcon />}
              disabled={loading || !isParkAvailable}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
        
        {/* Filter chips and status */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            size="small"
            startIcon={<FilterListIcon />}
            onClick={(e) => setFilterAnchorEl(e.currentTarget)}
            variant={statusFilter !== "all" ? "contained" : "outlined"}
            color="primary"
            disabled={!isParkAvailable || loading}
          >
            Status Filter
          </Button>
          
          {statusFilter !== "all" && (
            <Chip
              label={`Status: ${statusFilter}`}
              onDelete={() => applyStatusFilter("all")}
              size="small"
              color="primary"
            />
          )}
          
          {(searchQuery || statusFilter !== "all") && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              variant="outlined"
              disabled={!isParkAvailable || loading}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Paper>

      {/* Status filter menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
      >
        <MenuItem onClick={() => applyStatusFilter("all")}>
          All Status
        </MenuItem>
        
        <MenuItem onClick={() => applyStatusFilter("approved")}>
          <Chip label="APPROVED" color="success" size="small" sx={{ mr: 1 }} />
          Approved
        </MenuItem>
        <MenuItem onClick={() => applyStatusFilter("declined")}>
          <Chip label="DECLINED" color="error" size="small" sx={{ mr: 1 }} />
          Declined
        </MenuItem>
        <MenuItem onClick={() => applyStatusFilter("requested")}>
          <Chip label="REQUESTED" color="info" size="small" sx={{ mr: 1 }} />
          Requested
        </MenuItem>
      </Menu>

      {/* Action menu */}
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={handleActionMenuClose}
      >
        <MenuItem onClick={() => handleViewDetails(selectedRow)}>
          <ViewIcon sx={{ mr: 1, fontSize: 20 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleApproveClick} disabled={selectedRow?.status === "approved"}>
          <CheckCircleIcon sx={{ mr: 1, fontSize: 20, color: 'success.main' }} />
          Approve
        </MenuItem>
        <MenuItem onClick={handleDeclineClick} disabled={selectedRow?.status === "declined"}>
          <CancelIcon sx={{ mr: 1, fontSize: 20, color: 'error.main' }} />
          Decline
        </MenuItem>
      </Menu>

      {/* Results Section - Now uses actual 'no' from API */}
      <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3 }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>No</TableCell>
                <TableCell>Investor ID</TableCell>
                <TableCell>Recorded On</TableCell>
                <TableCell>Employment</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Activity Period</TableCell>
                <TableCell>Total Employees</TableCell>
                <TableCell>Park ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isParkAvailable ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Loading user information...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Loading investor activity records...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : searchResults.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {searchQuery || statusFilter !== "all" 
                        ? 'No records found for your search criteria. Try a different search term.' 
                        : 'No investor activity records found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((record) => (
                  <TableRow 
                    key={record.no} // Uses actual 'no' from API
                    hover
                    sx={{ 
                      backgroundColor: editingId === record.no ? 'action.selected' : 'inherit',
                      '&:hover': {
                        backgroundColor: editingId === record.no ? 'action.selected' : 'action.hover'
                      }
                    }}
                  >
                    <TableCell>{record.no}</TableCell> {/* Actual API 'no' */}
                    <TableCell>{record.investor_id}</TableCell>
                    
                    {/* Recorded On - Editable */}
                    <EditableTableCell
                      record={record}
                      field="recorded_on"
                      value={editingId === record.no ? editedRowData?.recorded_on : record.recorded_on}
                      isEditing={editingId === record.no}
                      onFieldChange={handleFieldChange}
                      fieldType="date"
                    />
                    
                    {/* Employment - Editable */}
                    <EditableTableCell
                      record={record}
                      field="employment"
                      value={editingId === record.no ? editedRowData?.employment : record.employment}
                      isEditing={editingId === record.no}
                      onFieldChange={handleFieldChange}
                      fieldType="number"
                    />
                    
                    {/* Description - Editable */}
                    <EditableTableCell
                      record={record}
                      field="description"
                      value={editingId === record.no ? editedRowData?.description : record.description}
                      isEditing={editingId === record.no}
                      onFieldChange={handleFieldChange}
                      fieldType="text"
                    />
                    
                    {/* Activity Period - Show as From-To */}
                    <TableCell>
                      {editingId === record.no ? (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            size="small"
                            type="date"
                            value={editedRowData?.activity_period_start_from ? 
                              new Date(editedRowData.activity_period_start_from).toISOString().split('T')[0] : ''}
                            onChange={(e) => handleFieldChange("activity_period_start_from", e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            label="From"
                          />
                          <TextField
                            size="small"
                            type="date"
                            value={editedRowData?.activity_period_to ? 
                              new Date(editedRowData.activity_period_to).toISOString().split('T')[0] : ''}
                            onChange={(e) => handleFieldChange("activity_period_to", e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            label="To"
                          />
                        </Box>
                      ) : (
                        `${record.activity_period_start_from_formatted} - ${record.activity_period_to_formatted}`
                      )}
                    </TableCell>
                    
                    {/* Total Employees */}
                    <TableCell>
                    {calculateTotalEmployment(record)}
                    </TableCell>
                    
                    <TableCell>{record.park_id}</TableCell>
                    
                    {/* Status - Editable */}
                    <TableCell>
                      {editingId === record.no ? (
                        <Select
                          size="small"
                          value={editedRowData?.status || 'pending'}
                          onChange={(e) => handleFieldChange("status", e.target.value)}
                          sx={{ width: '120px' }}
                        >
                          <MenuItem value="approved">Approved</MenuItem>
                          <MenuItem value="declined">Declined</MenuItem>
                          <MenuItem value="requested">Requested</MenuItem>
                        </Select>
                      ) : (
                        <Chip
                          label={(record.status || "pending").toUpperCase()}
                          color={getChipColor(record.status || "pending")}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    
                    {/* Actions - Uses actual 'no' from API */}
                    <TableCell align="center">
                      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                        {editingId === record.no ? (
                          // Show Save and Cancel buttons when in edit mode
                          <>
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={handleSaveInlineEdit}
                              title="Save Changes"
                              disabled={isProcessing}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={handleCancelInlineEdit}
                              title="Cancel Edit"
                              disabled={isProcessing}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          // Show regular action buttons when not in edit mode
                          <>
                            <IconButton 
                              size="small" 
                              color="info"
                              onClick={() => handleViewRecord(record)}
                              title="View Details"
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleInlineEdit(record)}
                              title="Edit"
                              disabled={record.status === "approved" || record.status === "declined"}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                           
                            <IconButton 
                              size="small" 
                              onClick={(e) => handleActionMenuOpen(e, record)}
                              title="More Actions"
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          disabled={!isParkAvailable}
        />
      </Paper>

      {/* View Record Dialog - Uses actual 'no' from API */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Investor Activity Details
          {selectedRow && (
            <Chip 
              label={`No: ${selectedRow.no}`} 
              size="small" 
              color="primary"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedRow && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">No</Typography>
                <Typography variant="body1">{selectedRow.no}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Investor ID</Typography>
                <Typography variant="body1">{selectedRow.investor_id}</Typography>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Recorded On</Typography>
                <Typography variant="body1">{selectedRow.recorded_on_formatted}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Employment</Typography>
                <Typography variant="body1">{selectedRow.employment}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Park ID</Typography>
                <Typography variant="body1">{selectedRow.park_id}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Activity Period</Typography>
                <Typography variant="body1">
                  {selectedRow.activity_period_start_from_formatted} to {selectedRow.activity_period_to_formatted}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                <Chip 
                  label={(selectedRow.status || "pending").toUpperCase()} 
                  color={getChipColor(selectedRow.status || "pending")}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedRow.description || 'No description provided'}
                </Typography>
              </Grid>
              
              {/* Employment Details */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Employment Breakdown
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MaleIcon color="primary" />
                      <Typography variant="body2">Professional Male: {selectedRow.emp_prof_male || 0}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FemaleIcon color="secondary" />
                      <Typography variant="body2">Professional Female: {selectedRow.emp_prof_female || 0}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MaleIcon color="primary" />
                      <Typography variant="body2">Labor Male: {selectedRow.emp_labor_male || 0}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FemaleIcon color="secondary" />
                      <Typography variant="body2">Labor Female: {selectedRow.emp_labor_female || 0}</Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                  Total Employees: {calculateTotalEmployment(selectedRow)}
                </Typography>
              </Grid>
              
              {/* Financial Details */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Financial Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">Export Amount: {selectedRow.export_amount || 0}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">Import Substitution: {selectedRow.import_substitution || 0}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">Import: {selectedRow.import || 0}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">Linkage Money: {selectedRow.linkage_money || 0}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">Linkage: {selectedRow.linkage || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Grid>
              
              {/* Attachment */}
              {selectedRow.attachment_path && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="textSecondary">Attachment</Typography>
                  <Button
                    startIcon={<AttachFileIcon />}
                    onClick={() => window.open(selectedRow.attachment_path, '_blank')}
                    size="small"
                  >
                    View Attachment
                  </Button>
                </Grid>
              )}
              
              {/* Remarks */}
              {selectedRow.remarks && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="textSecondary">Remarks</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedRow.remarks}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
          
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog 
        open={approvalDialogOpen} 
        onClose={() => {
          setApprovalDialogOpen(false);
          setSelectedRow(null);
          setRemarks("");
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: 'success.main' }} />
            <Typography>Approve Investor Activity</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRow && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="textSecondary">Investor ID:</Typography>
              <Typography variant="body1" fontWeight="medium">{selectedRow.investor_id}</Typography>
              
              <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
                Description:
              </Typography>
              <Typography variant="body1">{selectedRow.description || 'No description'}</Typography>
            </Box>
          )}
          
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to Approve this investor activity record?
          </Typography>
          
          <TextField
            label="Remarks (Optional)"
            fullWidth
            multiline
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Add any remarks or comments..."
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setApprovalDialogOpen(false);
              setSelectedRow(null);
              setRemarks("");
            }} 
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleVerifyConfirm} 
            variant="contained" 
            sx={{ 
              minWidth: 120,
              backgroundColor: '#4caf50',
              '&:hover': { backgroundColor: '#388e3c' }
            }}
            startIcon={<CheckCircleIcon />}
            disabled={isProcessing}
          >
            {isProcessing ? <CircularProgress size={24} /> : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decline Dialog */}
      <Dialog 
        open={declineDialogOpen} 
        onClose={() => {
          setDeclineDialogOpen(false);
          setSelectedRow(null);
          setRemarks("");
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon sx={{ color: 'error.main' }} />
            <Typography>Decline Investor Activity</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRow && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="textSecondary">Investor ID:</Typography>
              <Typography variant="body1" fontWeight="medium">{selectedRow.investor_id}</Typography>
              
              <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
                Description:
              </Typography>
              <Typography variant="body1">{selectedRow.description || 'No description'}</Typography>
            </Box>
          )}
          
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to decline this investor activity record?
          </Typography>
          
          <TextField
            label="Reason for Decline"
            fullWidth
            multiline
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Please provide reason for declining..."
            required
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setDeclineDialogOpen(false);
              setSelectedRow(null);
              setRemarks("");
            }} 
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeclineConfirm} 
            variant="contained" 
            sx={{ 
              minWidth: 120,
              backgroundColor: '#f44336',
              '&:hover': { backgroundColor: '#d32f2f' }
            }}
            startIcon={<CancelIcon />}
            disabled={isProcessing || !remarks.trim()}
          >
            {isProcessing ? <CircularProgress size={24} /> : "Decline"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request More Info Dialog */}
      <Dialog 
        open={requestDialogOpen} 
        onClose={() => {
          setRequestDialogOpen(false);
          setSelectedRow(null);
          setRemarks("");
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: 'warning.main' }} />
            <Typography>Request More Information</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRow && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="textSecondary">Investor ID:</Typography>
              <Typography variant="body1" fontWeight="medium">{selectedRow.investor_id}</Typography>
              
              <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
                Description:
              </Typography>
              <Typography variant="body1">{selectedRow.description || 'No description'}</Typography>
            </Box>
          )}
          
          <Typography sx={{ mb: 2 }}>
            What additional information do you need?
          </Typography>
          
          <TextField
            label="Request Details"
            fullWidth
            multiline
            rows={4}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Specify what additional information or documents are needed..."
            required
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setRequestDialogOpen(false);
              setSelectedRow(null);
              setRemarks("");
            }} 
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRequestConfirm} 
            variant="contained" 
            sx={{ 
              minWidth: 140,
              backgroundColor: '#ff9800',
              '&:hover': { backgroundColor: '#f57c00' }
            }}
            startIcon={<InfoIcon />}
            disabled={isProcessing || !remarks.trim()}
          >
            {isProcessing ? <CircularProgress size={24} /> : "Send Request"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}